import {KeyboardEvent, useId, useState} from 'react';

import {Nullish, TestIdProps} from 'shared/web';

import {Tab, TabProps, TabVariants} from '../Tabs/Tab';
import {StyledTabList} from '../Tabs/Tabs';

type HeaderTabs = Omit<TabProps, 'content' | 'actions'>;

export type TabsHeaderProps = TestIdProps & {
  variant?: TabVariants;
  tabs: HeaderTabs[];
  activeTabId: string | Nullish;
  isFullWidth?: boolean;
  onChange: (id: string) => void;
};

export function TabsHeader(props: TabsHeaderProps) {
  const tabIdPrefix = useId();
  const activeTabId = props.activeTabId ?? props.tabs[0]?.id;

  // Track focused tab separately from active tab
  const [focusedTabId, setFocusedTabId] = useState<string | Nullish>(null);

  const handleChangeTab = (tabId: string) => {
    props.onChange(tabId);
  };

  const handleFocusTab = (tabId: string) => {
    setFocusedTabId(tabId);
  };

  const getTabId = (tabId: string) => `${tabIdPrefix}-tab-${tabId}`;

  const handleKeyDown = (event: KeyboardEvent<HTMLDivElement>) => {
    const enabledTabs = props.tabs.filter((tab) => !tab.isDisabled);
    const currentFocusedTab = focusedTabId ?? activeTabId;
    const currentIndex = enabledTabs.findIndex((tab) => tab.id === currentFocusedTab);

    if (currentIndex === -1) {
      return;
    }

    const focusTab = (tabId: string) => {
      const tabElement = document.getElementById(getTabId(tabId));
      if (tabElement) {
        tabElement.focus();
      }
    };

    switch (event.key) {
      case 'ArrowLeft':
        event.preventDefault();
        const prevIndex = currentIndex === 0 ? enabledTabs.length - 1 : currentIndex - 1;
        const prevTab = enabledTabs.at(prevIndex);
        if (prevTab && prevIndex >= 0 && prevIndex < enabledTabs.length) {
          handleFocusTab(prevTab.id);
          focusTab(prevTab.id);
        }
        break;
      case 'ArrowRight':
        event.preventDefault();
        const nextIndex = currentIndex === enabledTabs.length - 1 ? 0 : currentIndex + 1;
        const nextTab = enabledTabs.at(nextIndex);
        if (nextTab && nextIndex >= 0 && nextIndex < enabledTabs.length) {
          handleFocusTab(nextTab.id);
          focusTab(nextTab.id);
        }
        break;
      case 'Home':
        event.preventDefault();
        const firstTab = enabledTabs.at(0);
        if (firstTab) {
          handleFocusTab(firstTab.id);
          focusTab(firstTab.id);
        }
        break;
      case 'End':
        event.preventDefault();
        const lastTab = enabledTabs.at(-1);
        if (lastTab) {
          handleFocusTab(lastTab.id);
          focusTab(lastTab.id);
        }
        break;
      case ' ':
      case 'Enter':
        event.preventDefault();
        if (currentFocusedTab) {
          handleChangeTab(currentFocusedTab);
          // Update focus to match the newly activated tab
          setFocusedTabId(currentFocusedTab);
        }
        break;
    }
  };

  return (
    <StyledTabList
      role="tablist"
      onKeyDown={handleKeyDown}
      $variant={props.variant}
      $isFullWidth={props.isFullWidth}
    >
      {props.tabs.map((tab) => (
        <Tab
          key={tab.id}
          id={tab.id}
          title={tab.title}
          icon={tab.icon}
          isDisabled={tab.isDisabled}
          variant={props.variant}
          isSelected={tab.id === activeTabId}
          onClick={handleChangeTab}
          onFocus={handleFocusTab}
          isFullWidth={props.isFullWidth}
          data-testid={tab['data-testid']}
          data-tab-id={tab.id}
          tabId={getTabId(tab.id)}
          tabIndex={tab.id === activeTabId ? 0 : -1}
        />
      ))}
    </StyledTabList>
  );
}
